import { ReactNode } from "react";

import { Modal, ModalProps } from "antd";
import { clsx } from "clsx";

import styles from "./RVModal.module.scss";

export const RVModalSizeOptions = {
    SMALL: "small",
    MEDIUM: "medium",
    LARGE: "large",
} as const;

export type RVModalSize = (typeof RVModalSizeOptions)[keyof typeof RVModalSizeOptions];

export type RVModalProps = Omit<ModalProps, "width" | "title"> & {
    /** Predefined modal width. Defaults to "small" (576px) */
    size?: RVModalSize;
    /** Title string */
    title?: string;
    /** Custom title component — overrides default title rendering */
    titleComponent?: ReactNode;
};

const SIZE_MAP: Record<RVModalSize, number> = {
    small: 576,
    medium: 846,
    large: 1146,
};

/**
 * Replaces antd's Modal with some preset sizes and stylings for consistency and alignment to the design system.
 */
export function RVModal({
    size = RVModalSizeOptions.SMALL,
    title,
    titleComponent,
    className,
    children,
    ...rest
}: RVModalProps): JSX.Element {
    return (
        // eslint-disable-next-line no-restricted-syntax -- RVModal wraps antd Modal by design
        <Modal
            title={titleComponent ?? (title ? <span className={styles.title}>{title}</span> : undefined)}
            width={SIZE_MAP[size]}
            className={clsx(
                styles.modalContainer,
                {
                    [styles.customFooter]: !!rest.footer,
                },
                className
            )}
            {...rest}
        >
            {children}
        </Modal>
    );
}
